import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Search, Upload, Download, Loader2, MapPin, ArrowRight, X, Check, Truck,
  Power, RotateCcw, FileSpreadsheet, Plus,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { useAuth, can, isRestricted } from '@/hooks/useAuth';

// Capabilities that can be bulk enabled/disabled on selected pincode rows.
const BULK_FIELDS = [
  { field: 'isCodAllowed', label: 'COD' },
  { field: 'isPrepaidAllowed', label: 'Prepaid' },
  { field: 'isPickupAllowed', label: 'Pickup' },
  { field: 'isReverseAllowed', label: 'Reverse' },
  { field: 'isOda', label: 'ODA' },
  { field: 'isActive', label: 'Active' },
] as const;

interface Courier { id: number; name: string; code: string; orderType?: string }
interface Row {
  id: number; courierId: number; pincode: string;
  city?: string; state?: string; zone?: string; mode?: string; tatDays?: number;
  isCodAllowed: boolean; isPrepaidAllowed: boolean; isPickupAllowed: boolean; isReverseAllowed: boolean; isOda: boolean; isActive: boolean;
  courier?: { id: number; name: string; code: string };
}
interface LaneItem {
  courierId: number;
  courier: { id: number; name: string; code: string; brand?: string };
  zone?: string; tatDays?: number; mode?: string;
  capabilities: { cod: boolean; prepaid: boolean; reverse: boolean; oda: boolean };
}

/** Parse any pasted blob into unique 6-digit pincodes — splits every 6 digits, ignoring all
 *  separators (spaces, commas, newlines, tabs). "110001400001" -> ["110001","400001"]. */
function parseBulkPincodes(raw: string): string[] {
  const out: string[] = [];
  for (const tok of raw.match(/\d+/g) ?? []) {
    for (let i = 0; i < tok.length; i += 6) { const c = tok.slice(i, i + 6); if (c) out.push(c); }
  }
  return Array.from(new Set(out));
}

export function ServiceabilityPage() {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  // Show write controls only where the user's role grants that action. Admins and grandfathered
  // (no-role) users are unrestricted and keep every control — mirrors the backend PermissionsGuard.
  const restricted = isRestricted(user);
  const canAdd = !restricted || can(user, 'Serviceability', 'All', 'add');
  const canEdit = !restricted || can(user, 'Serviceability', 'All', 'edit');
  const canExport = !restricted || can(user, 'Serviceability', 'All', 'export');
  const [source, setSource] = useState('');
  const [dest, setDest] = useState('');
  const [laneResult, setLaneResult] = useState<{ source: string; destination: string; items: LaneItem[]; count?: number } | null>(null);

  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [pincodeRaw, setPincodeRaw] = useState('');
  const parsedPins = useMemo(() => parseBulkPincodes(pincodeRaw), [pincodeRaw]);
  const pincodeRef = useRef<HTMLTextAreaElement | null>(null);

  // Grow the box with its contents — a column pasted out of Excel is dozens of lines and a
  // fixed 2-row box hides all but the first two. Capped, then it scrolls.
  useEffect(() => {
    const el = pincodeRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 46), 200)}px`;
  }, [pincodeRaw]);

  function applyPincodes(raw: string) {
    setPincodeRaw(raw);
    const parsed = parseBulkPincodes(raw);
    setPincodeFilter(parsed.length > 1 ? parsed.join(',') : (parsed[0] ?? ''));
    setPage(1);
  }

  /** Paste is where a newline-separated column arrives — fold it into `1,2,3` on the way in
   *  rather than leaving the user to reformat it. Typed input is left alone so a half-typed
   *  pincode doesn't get a comma pushed into it mid-keystroke; blur tidies that up. */
  function handlePincodePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    e.preventDefault();
    const el = e.currentTarget;
    const merged = el.value.slice(0, el.selectionStart ?? 0) + text + el.value.slice(el.selectionEnd ?? 0);
    applyPincodes(parseBulkPincodes(merged).join(','));
  }
  const [capFilter, setCapFilter] = useState<{ cod: '' | '1' | '0'; prepaid: '' | '1' | '0'; pickup: '' | '1' | '0'; reverse: '' | '1' | '0'; oda: '' | '1' | '0' }>({
    cod: '', prepaid: '', pickup: '', reverse: '', oda: '',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: couriers } = useQuery({
    queryKey: ['couriers', 'all'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data,
  });

  const { data: list, isLoading } = useQuery({
    queryKey: ['serviceability', { courierIds, pincodeFilter, capFilter, page, pageSize }],
    queryFn: async () => {
      const params: any = { page, pageSize };
      if (courierIds.length === 1) params.courierId = courierIds[0];
      else if (courierIds.length > 1) params.courierIds = courierIds.join(',');
      if (pincodeFilter) params.pincode = pincodeFilter;
      if (capFilter.cod) params.cod = capFilter.cod;
      if (capFilter.prepaid) params.prepaid = capFilter.prepaid;
      if (capFilter.pickup) params.pickup = capFilter.pickup;
      if (capFilter.reverse) params.reverse = capFilter.reverse;
      if (capFilter.oda) params.oda = capFilter.oda;
      return (await api.get<{ total: number; items: Row[] }>('/serviceability', { params })).data;
    },
  });

  const checkLane = useMutation({
    mutationFn: async () => (await api.get('/serviceability/check', { params: { source, destination: dest } })).data,
    onSuccess: (data) => { setLaneResult(data); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Lookup failed'),
  });

  const toggleField = useMutation({
    mutationFn: async ({ id, field, value }: { id: number; field: string; value: boolean }) =>
      (await api.patch(`/serviceability/${id}`, { [field]: value })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['serviceability'] }),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });

  const bulkAct = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: boolean }) =>
      (await api.post('/serviceability/bulk', { ids: selectedIds, set: { [field]: value } })).data as { updated: number },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['serviceability'] });
      const label = BULK_FIELDS.find(f => f.field === vars.field)?.label ?? vars.field;
      toast.success(`${vars.value ? 'Enabled' : 'Disabled'} ${label} on ${res.updated} pincode${res.updated === 1 ? '' : 's'}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Bulk update failed'),
  });

  function reset() {
    setCourierIds([]); setPage(1); applyPincodes('');
    setCapFilter({ cod: '', prepaid: '', pickup: '', reverse: '', oda: '' });
  }
  // Export follows what is on screen — courier AND pincode AND the capability filters. It used
  // to send the courier alone, so a screen narrowed to nine pincodes still exported the whole
  // 568k-row matrix.
  const exportQuery = (() => {
    const p = new URLSearchParams();
    if (courierIds.length === 1) p.set('courierId', String(courierIds[0]));
    else if (courierIds.length > 1) p.set('courierIds', courierIds.join(','));
    if (pincodeFilter) p.set('pincode', pincodeFilter);
    for (const k of ['cod', 'prepaid', 'pickup', 'reverse', 'oda'] as const) if (capFilter[k]) p.set(k, capFilter[k]);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  })();
  function downloadCsv(path: string, filename: string) {
    downloadBlob(path, filename);
  }

  const total = list?.total ?? 0;
  const pageIds = (list?.items ?? []).map(r => r.id);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Serviceability</h1>
          <p className="text-sm text-slate-500">Per-courier pincode matrix — pickup, COD, prepaid, reverse, ODA</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <button onClick={() => downloadCsv('/serviceability/export' + exportQuery, `serviceability-${new Date().toISOString().slice(0,10)}.csv`)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
              <Download size={14} /> Export Pincodes
            </button>
          )}
          {canAdd && (
            <button onClick={() => setShowAdd(true)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
              <Plus size={14} /> Add Pincode
            </button>
          )}
          {canAdd && (
            <button onClick={() => setShowUpload(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
              <Upload size={14} /> Upload Pincodes
            </button>
          )}
        </div>
      </div>

      {/* Source -> Destination lookup */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 mb-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-emerald-900 mb-1 block flex items-center gap-1"><MapPin size={12} /> Source Pincode (pickup origin)</label>
            <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. 110001" maxLength={6}
              className="input w-full bg-white font-mono" />
          </div>
          <div className="pb-2"><ArrowRight size={18} className="text-emerald-600" /></div>
          <div className="flex-1">
            <label className="text-xs font-medium text-emerald-900 mb-1 block flex items-center gap-1"><MapPin size={12} /> Destination Pincode</label>
            <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="e.g. 400001" maxLength={6}
              className="input w-full bg-white font-mono" />
          </div>
          <button onClick={() => checkLane.mutate()} disabled={!source || !dest || checkLane.isPending}
            className="px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5">
            {checkLane.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check Lane
          </button>
          {laneResult && (
            <button onClick={() => setLaneResult(null)} className="px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100 rounded-lg flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {laneResult && (
          <div className="mt-4 bg-white border border-emerald-200 rounded-lg p-3">
            <div className="text-sm font-medium mb-2 text-slate-700">
              {laneResult.items.length > 0
                ? <>✅ <strong>{laneResult.items.length}</strong> couriers can ship <span className="font-mono">{laneResult.source}</span> → <span className="font-mono">{laneResult.destination}</span></>
                : <>❌ No couriers found for <span className="font-mono">{laneResult.source}</span> → <span className="font-mono">{laneResult.destination}</span></>}
            </div>
            {laneResult.items.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {laneResult.items.map(it => (
                  <div key={it.courierId} className="border border-slate-200 rounded-lg p-2 flex items-center gap-2">
                    <Truck size={14} className="text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.courier.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1.5">
                        <span>{it.tatDays != null ? `${it.tatDays}d` : 'NA'}</span>
                        {it.zone && <span>· {it.zone}</span>}
                        {it.capabilities.cod && <Badge txt="COD" />}
                        {it.capabilities.reverse && <Badge txt="REV" />}
                        {it.capabilities.oda && <Badge txt="ODA" tone="warn" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid grid-cols-6 gap-3 items-end">
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Courier(s)</label>
            <MultiSelectFilter
              label=""
              placeholder="All couriers"
              values={courierIds}
              onChange={(vals) => { setCourierIds(vals); setPage(1); }}
              options={(couriers ?? []).map(c => ({ value: c.id, label: `#${c.id} · ${c.name}` }))}
            />
          </div>
          <div className="col-span-3">
            <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center justify-between">
              <span>Pincode(s) — paste in bulk</span>
              {parsedPins.length > 0 && <span className="text-emerald-700 font-semibold">{parsedPins.length} pincode{parsedPins.length === 1 ? '' : 's'}</span>}
            </label>
            <textarea
              ref={pincodeRef}
              value={pincodeRaw}
              onChange={(e) => applyPincodes(e.target.value)}
              onPaste={handlePincodePaste}
              onBlur={() => {
                const norm = parseBulkPincodes(pincodeRaw).join(',');
                if (norm !== pincodeRaw) applyPincodes(norm);
              }}
              rows={2}
              placeholder="Paste pincodes in any format — one per line, 110001 400001 700016, or 110001,400001, or a single prefix like 11"
              title="Paste digits/pincodes in any format — auto-split into 6-digit pincodes, comma-separated"
              className="input w-full font-mono text-xs resize-none overflow-y-auto"
            />
            <div className="text-[11px] text-slate-400 mt-0.5">{parsedPins.length > 1 ? `Searching ${parsedPins.length} pincodes` : parsedPins.length === 1 ? 'Prefix / single-pincode search' : 'Auto-splits pasted digits into 6-digit pincodes'}</div>
          </div>
          {(['cod','prepaid','pickup','reverse','oda'] as const).map(k => (
            <div key={k} className="col-span-[0.6]">
              <label className="text-xs font-medium text-slate-500 mb-1 block uppercase">{k}</label>
              <select value={capFilter[k]} onChange={(e) => { setCapFilter({ ...capFilter, [k]: e.target.value as any }); setPage(1); }} className="input w-full">
                <option value="">Any</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={reset} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1.5"><RotateCcw size={12} /> Reset</button>
          <div className="ml-auto text-xs text-slate-500">{total.toLocaleString('en-IN')} rows</div>
        </div>
      </div>

      {/* Bulk actions toolbar — appears when rows are selected (edit permission required) */}
      {canEdit && selectedIds.length > 0 && (
        <div className="bg-primary/5 border border-primary/30 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{selectedIds.length} selected:</span>
          {BULK_FIELDS.map(f => (
            <span key={f.field} className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden text-xs">
              <span className="px-2 py-1 text-slate-500 border-r border-slate-200">{f.label}</span>
              <button disabled={bulkAct.isPending} onClick={() => bulkAct.mutate({ field: f.field, value: true })} className="px-2 py-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">On</button>
              <button disabled={bulkAct.isPending} onClick={() => bulkAct.mutate({ field: f.field, value: false })} className="px-2 py-1 text-rose-600 hover:bg-rose-50 border-l border-slate-200 disabled:opacity-40">Off</button>
            </span>
          ))}
          <button onClick={() => setSelectedIds([])} className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><X size={12} /> Clear selection</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {canEdit && (
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
                        else setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
                      }} title="Select all on this page" />
                  </th>
                )}
                <th className="text-left px-3 py-2.5">Courier</th>
                <th className="text-left px-3 py-2.5">Pincode</th>
                <th className="text-left px-3 py-2.5">City / State</th>
                <th className="text-left px-3 py-2.5">Zone</th>
                <th className="text-left px-3 py-2.5">TAT</th>
                <th className="text-center px-3 py-2.5">COD</th>
                <th className="text-center px-3 py-2.5">Prepaid</th>
                <th className="text-center px-3 py-2.5">Pickup</th>
                <th className="text-center px-3 py-2.5">Reverse</th>
                <th className="text-center px-3 py-2.5">ODA</th>
                <th className="text-center px-3 py-2.5">Active</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={canEdit ? 12 : 11} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && (list?.items ?? []).length === 0 && (<tr><td colSpan={canEdit ? 12 : 11} className="text-center py-12 text-slate-400">No serviceability rows for these filters.</td></tr>)}
              {(list?.items ?? []).map(r => (
                <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${selectedIds.includes(r.id) ? 'bg-primary/5' : ''}`}>
                  {canEdit && (
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={selectedIds.includes(r.id)}
                        onChange={() => setSelectedIds(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id])} />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium text-slate-900">{r.courier?.name ?? `#${r.courierId}`}</td>
                  <td className="px-3 py-2 font-mono">{r.pincode}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.zone || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.tatDays != null ? `${r.tatDays}d` : 'NA'}</td>
                  {(['isCodAllowed','isPrepaidAllowed','isPickupAllowed','isReverseAllowed','isOda','isActive'] as const).map(field => (
                    <td key={field} className="px-3 py-2 text-center">
                      <Toggle on={(r as any)[field]} disabled={!canEdit} onChange={(v) => toggleField.mutate({ id: r.id, field, value: v })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="border border-slate-200 rounded px-1.5 py-1 bg-white">
                {[25, 50, 100, 200, 500].map(n => (<option key={n} value={n}>{n}</option>))}
              </select>
              <span className="ml-2">
                {((page - 1) * pageSize + 1).toLocaleString('en-IN')}–{Math.min(page * pageSize, total).toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span>Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</span>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Previous</button>
              <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title="Upload Serviceability" subtitle="Upload pincodes for one or more couriers" maxWidth="2xl">
        <UploadDialog couriers={couriers ?? []} onClose={() => setShowUpload(false)} onSuccess={() => { qc.invalidateQueries({ queryKey: ['serviceability'] }); }} />
      </Modal>

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Pincode" subtitle="Add a single pincode — City & State auto-fill from the PIN" maxWidth="2xl">
        <AddPincodeDialog couriers={couriers ?? []} onClose={() => setShowAdd(false)} onSuccess={() => { qc.invalidateQueries({ queryKey: ['serviceability'] }); }} />
      </Modal>
    </div>
  );
}

function Badge({ txt, tone }: { txt: string; tone?: 'warn' }) {
  const cls = tone === 'warn' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{txt}</span>;
}

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button onClick={() => onChange(!on)} disabled={disabled}
      title={disabled ? 'You do not have permission to edit serviceability' : undefined}
      className={`h-6 w-6 rounded inline-flex items-center justify-center transition disabled:cursor-default disabled:hover:bg-transparent ${on ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'} ${disabled ? 'opacity-70' : ''}`}>
      {on ? <Check size={14} /> : <X size={14} />}
    </button>
  );
}

// ============ Add single pincode (with PIN → City/State autofill) ============
function AddPincodeDialog({ couriers, onClose, onSuccess }: { couriers: Courier[]; onClose: () => void; onSuccess: () => void }) {
  const [pincode, setPincode] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zone, setZone] = useState('');
  const [mode, setMode] = useState('SURFACE');
  const [tatDays, setTatDays] = useState('');
  const [flags, setFlags] = useState({ cod: true, prepaid: true, pickup: true, reverse: false, oda: false });
  const [picked, setPicked] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [autoStatus, setAutoStatus] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');

  // Auto-fetch City + State whenever a valid 6-digit PIN is typed.
  useEffect(() => {
    const p = pincode.trim();
    if (p.length !== 6 || !/^\d{6}$/.test(p)) { setAutoStatus('idle'); return; }
    let cancelled = false;
    setAutoStatus('loading');
    (async () => {
      try {
        const r = await api.get(`/pincodes/lookup/${p}`);
        if (cancelled) return;
        const d: any = r.data ?? {};
        const cty = d.city?.name ?? d.cityName ?? d.city ?? '';
        const st = d.state?.name ?? d.stateName ?? d.state ?? '';
        if (cty || st) { setCity(cty); setState(st); setAutoStatus('found'); }
        else setAutoStatus('notfound');
      } catch { if (!cancelled) setAutoStatus('notfound'); }
    })();
    return () => { cancelled = true; };
  }, [pincode]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return couriers;
    return couriers.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [couriers, search]);

  function toggle(id: number) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  const validPin = /^\d{6}$/.test(pincode.trim());

  const save = useMutation({
    mutationFn: async () => (await api.post('/serviceability/upload', {
      courierIds: picked,
      rows: [{
        pincode: pincode.trim(),
        cod: flags.cod ? 1 : 0, prepaid: flags.prepaid ? 1 : 0, pickup: flags.pickup ? 1 : 0,
        reverse: flags.reverse ? 1 : 0, oda: flags.oda ? 1 : 0,
        city, state, zone, mode, tatDays: tatDays === '' ? '' : Number(tatDays),
      }],
      dryRun: false,
    })).data,
    onSuccess: (data: any) => {
      toast.success(`Saved ${pincode.trim()} for ${data.couriers} courier(s)`);
      onSuccess(); onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Pincode *</label>
          <input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit PIN" maxLength={6} className="input w-full font-mono" />
          <div className="h-4 mt-1 text-xs">
            {autoStatus === 'loading' && <span className="text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Looking up city & state…</span>}
            {autoStatus === 'found' && <span className="text-emerald-600 flex items-center gap-1"><Check size={11} /> Auto-filled from PIN (editable)</span>}
            {autoStatus === 'notfound' && <span className="text-amber-600">PIN not in masters — enter City/State manually</span>}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">TAT (days)</label>
          <input value={tatDays} onChange={(e) => setTatDays(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 3" className="input w-full" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">City</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Auto-fills from PIN" className="input w-full" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">State</label>
          <input value={state} onChange={(e) => setState(e.target.value)} placeholder="Auto-fills from PIN" className="input w-full" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Zone</label>
          <input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="e.g. A" className="input w-full" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="input w-full">
            <option value="SURFACE">SURFACE</option>
            <option value="AIR">AIR</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2 block">Capabilities</label>
        <div className="flex flex-wrap gap-3">
          {(['cod', 'prepaid', 'pickup', 'reverse', 'oda'] as const).map(k => (
            <label key={k} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={flags[k]} onChange={() => setFlags(f => ({ ...f, [k]: !f[k] }))} className="rounded" />
              <span className="uppercase text-xs font-medium">{k}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Apply to couriers ({picked.length} selected)</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search couriers…" className="input h-8 text-xs w-48" />
        </div>
        <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
          {filtered.map(c => (
            <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} className="rounded" />
              <span className="text-sm">{c.name} (#{c.id})</span>
              <span className="text-xs text-slate-400 ml-auto">{c.code}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CANCEL</button>
        <button onClick={() => save.mutate()} disabled={!validPin || picked.length === 0 || save.isPending}
          className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save Pincode
        </button>
      </div>
    </div>
  );
}

// ============ Upload Dialog ============
function UploadDialog({ couriers, onClose, onSuccess }: { couriers: Courier[]; onClose: () => void; onSuccess: () => void }) {
  const [picked, setPicked] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [parsing, setParsing] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return couriers;
    return couriers.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [couriers, search]);

  function toggle(id: number) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  // Rows carry their own courier → the UI courier-pick becomes optional.
  const rowsHaveCourier = useMemo(
    () => rows.some(r => [r.couriercode, r.courier, r.couriername, r.courierid, r.courier_code, r.courier_name, r.courier_id]
      .some(v => v !== undefined && v !== null && String(v).trim() !== '')),
    [rows],
  );
  const canSubmit = rows.length > 0 && (picked.length > 0 || rowsHaveCourier);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    setParsing(true);
    try {
      const isCsv = file.name.toLowerCase().endsWith('.csv');
      if (!isCsv) {
        toast.error('XLSX upload coming soon — please save as CSV for now');
        setParsing(false);
        return;
      }
      const text = new TextDecoder().decode(await file.arrayBuffer());
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) throw new Error('CSV has no data rows');
      const headers = splitCsv(lines[0]).map(h => h.toLowerCase().trim());
      const parsedRows = lines.slice(1).map(line => {
        const cells = splitCsv(line);
        const obj: any = {};
        headers.forEach((h, i) => { obj[h] = cells[i]; });
        return obj;
      });
      setRows(parsedRows);
      setDryRunResult(null);
      toast.success(`Parsed ${parsedRows.length} rows`);
    } catch (err: any) {
      toast.error(`Parse error: ${err?.message ?? 'unknown'}`);
    } finally {
      setParsing(false);
    }
  }

  const submit = useMutation({
    /*
     * A generous timeout of its own, overriding the client's 30s default.
     *
     * A real courier sheet is over 20,000 pincodes and can be sent to several couriers at once. The
     * default cut the browser off long before the server had finished, so the screen said "Upload
     * failed" while the import was still running and went on to complete — and the natural response
     * to that message is to upload the whole sheet again.
     */
    mutationFn: async (dryRun: boolean) =>
      (await api.post('/serviceability/upload', { courierIds: picked, rows, dryRun }, { timeout: 15 * 60_000 })).data,
    onSuccess: (data, dryRun) => {
      if (dryRun) setDryRunResult(data);
      else {
        toast.success(`Uploaded ${data.upserted} pincode rows across ${data.couriers} courier(s)${data.duplicates ? ` · ${data.duplicates} duplicate row(s) merged` : ''}${data.errors?.length ? ` · ${data.errors.length} row(s) skipped` : ''}`);
        onSuccess(); onClose();
      }
    },
    onError: (e: any) => {
      // A timeout has NO response — and the import may well have finished anyway, so saying
      // "failed" would be a lie that invites a duplicate upload.
      const timedOut = e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message ?? ''));
      toast.error(timedOut
        ? 'This is taking longer than expected. The upload may still be finishing — close this and check the list before uploading again.'
        : (e?.response?.data?.message ?? 'Upload failed'));
    },
  });

  function downloadTemplate() {
    // Generate the template client-side and download synchronously inside the click.
    // (Fetching first would drop the user-gesture context and Chrome can then block
    // the save — the button "does nothing". This runs with no network/auth at all.)
    // Rows apply to the courier(s) ticked below. (city/state/courierCode columns removed.)
    const csv = [
      'pincode,cod,prepaid,pickup,reverse,oda,zone,mode,tatDays',
      '110001,1,1,1,1,0,A,SURFACE,2',
      '400001,1,1,1,1,0,A,SURFACE,3',
      '560001,1,1,1,0,0,A,SURFACE,3',
      '500001,1,1,0,0,1,B,SURFACE,4',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'serviceability-template.csv';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  return (
    <div className="space-y-5">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
        <div className="flex items-start gap-2">
          <FileSpreadsheet size={16} className="text-emerald-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-900 mb-0.5">Important Instructions</div>
            <p className="text-xs text-emerald-800">Required columns: <strong>pincode</strong>, <strong>cod</strong>, <strong>prepaid</strong>, <strong>pickup</strong>, <strong>reverse</strong>, <strong>oda</strong> (1=yes, 0=no). Optional: <em>zone, mode, tatDays</em>.</p>
            <p className="text-xs text-emerald-800 mt-1">Each row applies to the courier(s) you tick below.</p>
            <p className="text-xs text-emerald-800 mt-1"><strong>City and state are not imported</strong> — they come from the Pincode master. Leave them out; if your sheet has them they are ignored.</p>
            <button onClick={downloadTemplate} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">
              <Download size={12} /> Download Template
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Step 1 — Pick couriers ({picked.length} selected){rowsHaveCourier && <span className="ml-1 text-emerald-600 normal-case font-normal">— optional, your file has a courierCode column</span>}</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search couriers…"
            className="input h-8 text-xs w-48" />
        </div>
        <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
          {filtered.map(c => (
            <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} className="rounded" />
              <span className="text-sm">{c.name} (#{c.id})</span>
              <span className="text-xs text-slate-400 ml-auto">{c.code}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2 block">Step 2 — Upload pincode sheet</label>
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-primary transition cursor-pointer relative">
          <input type="file" accept=".csv" onChange={handleFile} className="absolute inset-0 opacity-0 cursor-pointer" />
          {parsing ? (
            <Loader2 size={24} className="mx-auto animate-spin text-primary" />
          ) : rows.length > 0 ? (
            <div>
              <Check size={24} className="mx-auto text-emerald-600 mb-1" />
              <div className="text-sm font-medium">{rows.length} rows parsed</div>
              <div className="text-xs text-slate-500">Click to re-upload</div>
            </div>
          ) : (
            <div>
              <Upload size={24} className="mx-auto text-slate-400 mb-1" />
              <div className="text-sm">Choose file or drag &amp; drop</div>
              <div className="text-xs text-slate-500 mt-0.5">Supported: .csv (max 10MB). XLSX support: save as CSV for now.</div>
            </div>
          )}
        </div>
      </div>

      {dryRunResult && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
          <strong>Dry-run preview:</strong> {dryRunResult.validRows} valid rows → {dryRunResult.operations ?? dryRunResult.validRows} pincode updates across {dryRunResult.couriers} courier(s).
          {dryRunResult.duplicates > 0 && <span> {dryRunResult.duplicates} duplicate pincode row(s) in the sheet — the last one of each is used.</span>}
          {dryRunResult.errors?.length > 0 && (
            <div className="mt-1">{dryRunResult.errors.length} error rows — first few:
              {dryRunResult.errors.slice(0, 5).map((e: any, i: number) => (
                <div key={i} className="font-mono">Row {e.row}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CANCEL</button>
        <button onClick={() => submit.mutate(true)} disabled={!canSubmit || submit.isPending}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
          Dry-run check
        </button>
        <button onClick={() => submit.mutate(false)} disabled={!canSubmit || submit.isPending}
          className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />} Upload Serviceability
        </button>
      </div>
    </div>
  );
}

function splitCsv(line: string): string[] {
  const out: string[] = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}
